import { supabase } from "@/integrations/supabase/client";
import { addFinancialBreadcrumb } from "@/lib/sentry";

interface CreateBillApprovalParams {
  companyId: string;
  requestType: string;
  title: string;
  projectId: string;
  linkedWoId?: string;
  linkedDrawId?: string;
  linkedBillId?: string;
  requestData?: Record<string, any>;
  createdBy?: string;
}

/**
 * Creates an approval_request + assigns Admin/Controller users as approvers
 * via a single atomic DB function. Call after inserting a bill or draw.
 */
export async function createBillApproval({
  companyId,
  requestType,
  title,
  projectId,
  linkedWoId,
  linkedDrawId,
  linkedBillId,
  requestData,
  createdBy,
}: CreateBillApprovalParams) {
  const { data, error } = await supabase.rpc("create_bill_approval" as any, {
    p_company_id: companyId,
    p_request_type: requestType,
    p_title: title,
    p_project_id: projectId,
    p_linked_wo_id: linkedWoId ?? null,
    p_linked_draw_id: linkedDrawId ?? null,
    p_linked_bill_id: linkedBillId ?? null,
    p_request_data: requestData ?? null,
    p_created_by: createdBy ?? null,
  });

  if (error) throw error;
  addFinancialBreadcrumb("bill_approval_created", { requestType, projectId, billId: linkedBillId, approvalId: data });

  // Write to ops_audit_log for cross-system audit trail
  try {
    await supabase.from("ops_audit_log").insert({
      company_id: companyId,
      table_name: "approval_requests",
      record_id: data,
      action: "bill_sent_for_approval",
      old_status: null,
      new_status: "pending",
      changed_by: createdBy ?? null,
      metadata: {
        request_type: requestType,
        title,
        linked_bill_id: linkedBillId ?? null,
        linked_wo_id: linkedWoId ?? null,
        linked_draw_id: linkedDrawId ?? null,
        project_id: projectId,
      },
    } as any);
  } catch {
    // Don't block main action if ops_audit_log write fails
  }

  return { id: data };
}
